import get from 'lodash.get'
import { IIMAEvent } from '../@types/video.js/videojs-ima'
import { IMA_SAMPLE } from '../constants/url'
import { Config, ICurrentVidConfig } from './config'
import { IBidResponse } from '../@types/prebid'
import { bidForceStatus } from './bidforcing'
import { IAdRequestConfig } from '../@types/ads'

export function getAdDataFromIMAEvent(event: IIMAEvent): {} {
  const ad = event.getAd()

  return {
    adId: ad.getAdId(),
    adSystem: ad.getAdSystem(),
    advertiserName: ad.getAdvertiserName(),
    contentType: ad.getContentType(),
    creativeAdId: ad.getCreativeAdId(),
    creativeId: ad.getCreativeId(),
    dealId: ad.getDealId(),
    description: ad.getDescription(),
    duration: ad.getDuration(),
    height: ad.getHeight(),
    mediaUrl: ad.getMediaUrl(),
    surveyUrl: ad.getSurveyUrl(),
    title: ad.getTitle(),
    traffickingParameters: ad.getTraffickingParameters(),
    traffickingParametersString: ad.getTraffickingParametersString(),
    universalAdIdRegistry: ad.getUniversalAdIdRegistry(),
    universalAdIdValue: ad.getUniversalAdIdValue(),
    vastMediaBitrate: ad.getVastMediaBitrate(),
    vastMediaHeight: ad.getVastMediaHeight(),
    vastMediaWidth: ad.getVastMediaWidth(),
    width: ad.getWidth()
  }
}

export async function getAdTag(
  config: Config,
  currentVidConfig: ICurrentVidConfig,
  adRequestConfig: IAdRequestConfig = { adBreakType: 'preroll', startDelay: 0 }
): Promise<{
  adTagUrl: string | false
  highBid?: IBidResponse
  adRequestConfig: IAdRequestConfig
}> {
  if (config.showTestAd || config.bidForceAd) {
    bidForceStatus(config, 'getAdTag forcing preroll demo video to play')
    return {
      adTagUrl: IMA_SAMPLE.VMAP_PREROLL,
      adRequestConfig
    }
  }

  const getAdTagAndHighBid: (
    videoOptions: {},
    extras: {},
    skipPrebid: boolean
  ) => Promise<{ adTagUrl: string; highBid: IBidResponse }> = get(
    window,
    '$mediavine.web.getVideoAdTagAndHighBid'
  )

  if (!getAdTagAndHighBid) {
    console.error('Methods for getting ad tags not found')
    return { adTagUrl: false, adRequestConfig }
  }

  const videoOptions = createOptions(config, currentVidConfig, adRequestConfig)
  const customParams = createCustomParams(config, adRequestConfig)
  const skipPrebid = config.bidForceNone

  const adTagResp = await getAdTagAndHighBid(
    videoOptions,
    customParams,
    skipPrebid
  )

  bidForceStatus(config, 'ad tag for bids force:', adTagResp)

  return {
    adTagUrl: adTagResp.adTagUrl,
    highBid: adTagResp.highBid,
    adRequestConfig
  }
}

export async function getNextAdTag(
  config: Config,
  currentVidConfig: ICurrentVidConfig,
  adRequestConfig: IAdRequestConfig = { adBreakType: 'preroll', startDelay: 0 }
): Promise<{
  adTagUrl: string | false
  highBid?: IBidResponse
  adRequestConfig: IAdRequestConfig
}> {
  if (config.showTestAd || config.bidForceAd) {
    bidForceStatus(config, 'getNextAdTag forcing preroll demo video to play')
    return {
      adTagUrl: IMA_SAMPLE.VMAP_PREROLL,
      adRequestConfig
    }
  }

  const getNextAdTagAndHighBid: (
    videoOptions: {},
    extras: {},
    skipPrebid: boolean
  ) => Promise<{ adTagUrl: string; highBid: IBidResponse }> = get(
    window,
    '$mediavine.web.getNextVideoAdTagAndHighBid'
  )

  if (!getNextAdTagAndHighBid) {
    console.error('Methods for getting ad tags not found')
    return { adTagUrl: false, adRequestConfig }
  }

  const videoOptions = createOptions(config, currentVidConfig, adRequestConfig)
  const customParams = createCustomParams(config, adRequestConfig)
  const skipPrebid = config.bidForceNone

  const adTagResp = await getNextAdTagAndHighBid(
    videoOptions,
    customParams,
    skipPrebid
  )

  bidForceStatus(config, 'ad tag for bids force:', adTagResp)

  return {
    adTagUrl: adTagResp.adTagUrl,
    highBid: adTagResp.highBid,
    adRequestConfig
  }
}

function createCustomParams(
  config: Config, 
  adRequestConfig: IAdRequestConfig
) {
  const preventAdxBidParams = { google: '0', test: ['houseads'].join(',') }
  const preventAdx = config.bidForceNone ? preventAdxBidParams : {}

  return {
    ...preventAdx,
    vspot: adRequestConfig.adBreakType
  }
}

function createOptions(
  config: Config,
  currentVidConfig: ICurrentVidConfig,
  adRequestConfig: IAdRequestConfig
) {
  return {
    adunitId: config.adunitId,
    autoplay: config.autoplay,
    contentHeight: config.contentHeight,
    contentWidth: config.contentWidth,
    manualOptions: config.domConfig,
    mimes: config.mimeTypes,
    mobile: config.mobile,
    npa: config.npa,
    omitFloorParams: config.bidForceNone,
    permalink: currentVidConfig.permalink,
    playlist: config.playlist,
    prebid: true,
    slug: currentVidConfig.slug,
    startDelay: adRequestConfig.startDelay,
    sticky: config.sticky,
    test: config.showTestAd,
    unitType: config.unitType,
    videoNumber: currentVidConfig.videoNumber,
    videosPlayed: currentVidConfig.videosPlayed
  }
}

export const adBreakConfigs = {
  preroll: {
    timeOffset: 'start',
    breakType: 'linear',
    breakId: 'preroll',
    allowMultipleAds: 'false',
    followRedirects: 'true'
  },
  midroll: {
    breakType: 'linear',
    breakId: 'midroll',
    allowMultipleAds: 'false',
    followRedirects: 'true'
  },
  nonlinear: {
    breakType: 'nonlinear',
    breakId: 'nonlinear',
    allowMultipleAds: 'false',
    followRedirects: 'true'
  }
}
